import { useState, useEffect, RefObject, useCallback, useRef } from 'react';
import { supabase } from '../config/supabase';
import { useAuth } from '../contexts/AuthContext';
import * as bodyPix from '@tensorflow-models/body-pix';
import '@tensorflow/tfjs';

interface UseWebcamCaptureProps {
  videoRef: RefObject<HTMLVideoElement | null>;
  canvasRef: RefObject<HTMLCanvasElement | null>;
  isEnabled: boolean;
  intervalMinutes?: number;
  alertId?: string;
  externalStream?: MediaStream | null;
}

interface ConnectionData {
  connection: RTCPeerConnection;
  pendingCandidates: RTCIceCandidate[];
}

export const useWebcamCapture = ({
  videoRef,
  canvasRef,
  isEnabled,
  intervalMinutes,
  alertId,
  externalStream
}: UseWebcamCaptureProps) => {
  const [error, setError] = useState<string | null>(null);
  const [hasWebcam, setHasWebcam] = useState(false);
  const { user } = useAuth();

  const peerConnections = useRef<Map<string, ConnectionData>>(new Map());

  const createPeerConnection = useCallback((supervisorId: string): ConnectionData => {
    console.log('Creando nueva conexión para supervisor:', supervisorId);
    const pc = new RTCPeerConnection({
      iceServers: [{ urls: 'stun:stun.l.google.com:19302' }]
    });

    const localStream = videoRef.current?.srcObject as MediaStream;
    if (localStream) {
      console.log('Agregando stream local a la conexión');
      localStream.getTracks().forEach(track => {
        pc.addTrack(track, localStream);
      });
    }

    pc.onicecandidate = async (event) => {
      if (event.candidate) {
        console.log('Enviando candidato ICE al supervisor:', supervisorId);
        try {
          const { error } = await supabase
            .from('webrtc_signaling')
            .insert({
              id: crypto.randomUUID(),
              from_user_id: user?.id,
              to_user_id: supervisorId,
              type: 'ice-candidate',
              payload: event.candidate.toJSON(),
              created_at: new Date().toISOString()
            });

          if (error) {
            console.error('Error al enviar candidato ICE:', error);
          }
        } catch (err) {
          console.error('Error al enviar candidato ICE:', err);
        }
      }
    };

    return { connection: pc, pendingCandidates: [] };
  }, [user?.id]);

  const processPendingCandidates = useCallback((supervisorId: string) => {
    const connectionData = peerConnections.current.get(supervisorId);
    if (connectionData) {
      const { connection: pc, pendingCandidates } = connectionData;
      
      if (pc.remoteDescription && pendingCandidates.length > 0) {
        console.log('Procesando candidatos ICE pendientes para supervisor:', supervisorId);
        pendingCandidates.forEach(async (candidate) => {
          try {
            await pc.addIceCandidate(candidate);
            console.log('Candidato ICE pendiente agregado');
          } catch (err) {
            console.error('Error al agregar candidato ICE pendiente:', err);
          }
        });
        connectionData.pendingCandidates = [];
      }
    }
  }, []);

  // Memoizar las funciones para evitar recreaciones
  const memoizedCreatePeerConnection = useCallback(createPeerConnection, []);
  const memoizedProcessPendingCandidates = useCallback(processPendingCandidates, []);

  useEffect(() => {
    let stream: MediaStream | null = null;
    let net: bodyPix.BodyPix | null = null;
    let animationFrameId: number;
    let tempCanvas: HTMLCanvasElement | null = null;
    let tempCtx: CanvasRenderingContext2D | null = null;

    const cleanup = () => {
      console.log('Limpiando recursos de la cámara en useWebcamCapture...');
      
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }

      if (tempCanvas) {
        tempCanvas.width = 0;
        tempCanvas.height = 0;
        tempCanvas = null;
      }
      tempCtx = null;

      if (stream) {
        stream.getTracks().forEach(track => {
          if (track.readyState === 'live') {
            track.stop();
            console.log('Track detenido en useWebcamCapture:', track.label);
          }
        });
        stream = null;
      }

      if (videoRef.current) {
        const video = videoRef.current;
        if (video.srcObject) {
          const videoStream = video.srcObject as MediaStream;
          videoStream.getTracks().forEach(track => {
            if (track.readyState === 'live') {
              track.stop();
              console.log('Track detenido del video element:', track.label);
            }
          });
          video.srcObject = null;
        }
        video.load();
      }

      if (canvasRef.current) {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.clearRect(0, 0, canvas.width, canvas.height);
        }
      }

      peerConnections.current.forEach((data) => {
        data.connection.close();
      });
      peerConnections.current.clear();

      setHasWebcam(false);
      net = null;
    };

    const initializeWebcam = async () => {
      if (!isEnabled) {
        cleanup();
        return;
      }

      try {
        // Solo cargar bodyPix si no está cargado
        if (!net) {
          net = await bodyPix.load({
            architecture: 'MobileNetV1',
            outputStride: 16,
            multiplier: 0.75,
            quantBytes: 2
          });
          console.log('Modelo BodyPix cargado');
        }

        // Solo crear stream si no existe
        if (!stream) {
          stream = await navigator.mediaDevices.getUserMedia({
            video: {
              width: { ideal: 640 },
              height: { ideal: 480 },
              frameRate: { ideal: 30 }
            }
          });
          console.log('Creado nuevo stream de cámara');
        }

        if (videoRef.current && !videoRef.current.srcObject) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
          
          tempCanvas = document.createElement('canvas');
          tempCanvas.width = videoRef.current.videoWidth;
          tempCanvas.height = videoRef.current.videoHeight;
          tempCtx = tempCanvas.getContext('2d');
          
          console.log('Cámara inicializada');
          setHasWebcam(true);
        }

        const processFrame = async () => {
          if (!isEnabled) {
            cleanup();
            return;
          }

          if (!videoRef.current || !canvasRef.current || !net || !tempCtx || !tempCanvas) {
            animationFrameId = requestAnimationFrame(processFrame);
            return;
          }

          const video = videoRef.current;
          const canvas = canvasRef.current;
          const ctx = canvas.getContext('2d', { willReadFrequently: true });
          if (!ctx) return;

          try {
            if (canvas.width !== video.videoWidth || canvas.height !== video.videoHeight) {
              canvas.width = video.videoWidth;
              canvas.height = video.videoHeight;
              tempCanvas.width = video.videoWidth;
              tempCanvas.height = video.videoHeight;
            }

            const segmentation = await net.segmentPerson(video, {
              flipHorizontal: false,
              internalResolution: 'medium',
              segmentationThreshold: 0.7
            });

            ctx.drawImage(video, 0, 0);
            tempCtx.drawImage(video, 0, 0);

            tempCtx.filter = 'blur(20px)';
            tempCtx.globalAlpha = 0.6;
            tempCtx.drawImage(tempCanvas, 0, 0);
            tempCtx.drawImage(tempCanvas, 0, 0);
            tempCtx.filter = 'blur(15px)';
            tempCtx.drawImage(tempCanvas, 0, 0);
            tempCtx.globalAlpha = 1.0;
            tempCtx.filter = 'none';

            const frame = ctx.getImageData(0, 0, canvas.width, canvas.height);
            const blurred = tempCtx.getImageData(0, 0, canvas.width, canvas.height);

            for (let i = 0; i < segmentation.data.length; i++) {
              const pixelIndex = i * 4;
              if (!segmentation.data[i]) { 
                frame.data[pixelIndex] = blurred.data[pixelIndex];
                frame.data[pixelIndex + 1] = blurred.data[pixelIndex + 1];
                frame.data[pixelIndex + 2] = blurred.data[pixelIndex + 2];
              }
            }

            ctx.putImageData(frame, 0, 0);

          } catch (err) {
            ctx.drawImage(video, 0, 0);
            console.error('Error al procesar frame:', err);
          }

          animationFrameId = requestAnimationFrame(processFrame);
        };

        processFrame();
        
        if (!isEnabled || !user?.id) return;

        const channelId = `webrtc:${user?.id}`;
        console.log('Creando canal:', channelId);

        const channel = supabase
          .channel(channelId)
          .on(
            'postgres_changes',
            {
              event: 'INSERT',
              schema: 'public',
              table: 'webrtc_signaling',
              filter: `to_user_id=eq.${user?.id}`
            },
            async (payload) => {
              const signal = payload.new as any;
              console.log('Señal recibida:', signal);

              try {
                if (signal.type === 'offer') {
                  console.log('Procesando oferta del supervisor:', signal.from_user_id);
                  let connectionData = peerConnections.current.get(signal.from_user_id);

                  if (!connectionData || connectionData.connection.connectionState === 'failed') {
                    connectionData = memoizedCreatePeerConnection(signal.from_user_id);
                    peerConnections.current.set(signal.from_user_id, connectionData);
                  }

                  const { connection: pc } = connectionData;

                  await pc.setRemoteDescription(new RTCSessionDescription(signal.payload));
                  console.log('Descripción remota establecida');

                  const answer = await pc.createAnswer();
                  await pc.setLocalDescription(answer);

                  const { error } = await supabase
                    .from('webrtc_signaling')
                    .insert({
                      id: crypto.randomUUID(),
                      from_user_id: user?.id,
                      to_user_id: signal.from_user_id,
                      type: 'answer',
                      payload: answer,
                      created_at: new Date().toISOString()
                    });

                  if (error) {
                    console.error('Error al enviar respuesta:', error);
                    return;
                  }

                  memoizedProcessPendingCandidates(signal.from_user_id);

                } else if (signal.type === 'ice-candidate') {
                  console.log('Procesando candidato ICE del supervisor:', signal.from_user_id);
                  const connectionData = peerConnections.current.get(signal.from_user_id);
                  const candidate = new RTCIceCandidate(signal.payload);

                  if (!connectionData) {
                    console.log('Guardando candidato ICE para más tarde');
                    const newConnectionData = memoizedCreatePeerConnection(signal.from_user_id);
                    newConnectionData.pendingCandidates.push(candidate);
                    peerConnections.current.set(signal.from_user_id, newConnectionData);
                    return;
                  }

                  const { connection: pc, pendingCandidates } = connectionData;
                  if (pc.remoteDescription) {
                    await pc.addIceCandidate(candidate);
                    console.log('Candidato ICE agregado');
                  } else {
                    console.log('Guardando candidato ICE (no hay descripción remota)');
                    pendingCandidates.push(candidate);
                  }
                }
              } catch (err) {
                console.error('Error al procesar señal:', err);
              }
            }
          )
          .subscribe(async (status) => {
            console.log('Estado de la suscripción:', status);
            if (status === 'SUBSCRIBED') {
              const { error: signalError } = await supabase
                .from('webrtc_signaling')
                .insert({
                  id: crypto.randomUUID(),
                  from_user_id: user?.id,
                  type: 'camera-ready',
                  payload: { status: 'active', channelId },
                  created_at: new Date().toISOString()
                });

              if (signalError) {
                console.error('Error al enviar señal de cámara activa:', signalError);
                return;
              }

              console.log('Señal de cámara activa enviada');

              const pc = new RTCPeerConnection({
                iceServers: [{ urls: 'stun:stun.l.google.com:19302' }]
              });

              const localStream = videoRef.current?.srcObject as MediaStream;
              if (localStream) {
                console.log('Agregando stream local a la conexión inicial');
                localStream.getTracks().forEach(track => {
                  pc.addTrack(track, localStream);
                });
              }

              pc.onicecandidate = async (event) => {
                if (event.candidate) {
                  console.log('Enviando candidato ICE inicial:', event.candidate);
                  try {
                    const { error } = await supabase
                      .from('webrtc_signaling')
                      .insert({
                        id: crypto.randomUUID(),
                        from_user_id: user?.id,
                        to_user_id: null, 
                        type: 'ice-candidate',
                        payload: event.candidate.toJSON(),
                        created_at: new Date().toISOString()
                      });

                    if (error) {
                      console.error('Error al enviar candidato ICE inicial:', error);
                    }
                  } catch (err) {
                    console.error('Error al enviar candidato ICE inicial:', err);
                  }
                }
              };

              const offer = await pc.createOffer({
                offerToReceiveVideo: false, 
                offerToReceiveAudio: false
              });
              await pc.setLocalDescription(offer);

              const { error } = await supabase
                .from('webrtc_signaling')
                .insert({
                  id: crypto.randomUUID(),
                  from_user_id: user?.id,
                  to_user_id: null, 
                  type: 'offer',
                  payload: offer,
                  created_at: new Date().toISOString()
                });

              if (error) {
                console.error('Error al enviar oferta inicial:', error);
                return;
              }

              peerConnections.current.set('initial', { 
                connection: pc, 
                pendingCandidates: [] 
              });
            }
          });

      } catch (err) {
        console.error('Error al inicializar la cámara:', err);
        setError('Error al inicializar la cámara');
      }
    };

    if (isEnabled) {
      initializeWebcam();
    }

    return () => {
      if (!isEnabled) {
        cleanup();
      }
    };
  }, [isEnabled]); // Solo depender de isEnabled

  return {
    error,
    hasWebcam
  };
};
